import { GetServerSidePropsContext } from "next";
import { useRouter } from "next/router";

import axios from "axios";

import { QueryClient } from "@enkor/react-query";

import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";
import EventDetailComponent from "@domains/event/views/eventDetail/EventDetail";
import { loadLocaleProps } from "@logics/utils/i18n";
import getTokenServerSession from "@domains/auth/utils/getTokenServerSession";
import GeneralHead from "@logics/tags/GeneralHead";
import { eventQueryKeys } from "@domains/event/constants/eventQueryKeys";
import { ResponseEventDetailV1 } from "@models/data-contracts";
import { ApiResponseDto } from "@models/client";
import { useGuardWebView } from "@logics/hooks/useGuardWebView";

interface EventDetailProps {
  eventId: string;
  eventDetail?: ResponseEventDetailV1;
}

const EventDetail = (props: EventDetailProps) => {
  const { replace, query } = useRouter();

  useGuardWebView({
    redirectCallback: () =>
      replace({
        pathname: NAVIGATION_ROUTE.LANDING_DETAIL.PATHNAME,
        query: {
          ...query,
          eventId: props.eventId,
        },
      }),
  });

  return (
    <>
      <GeneralHead
        page={{ title: `Enkoplay: ${props.eventDetail?.title}` }}
        openGraph={{ title: props.eventDetail?.title }}
      />

      <EventDetailComponent eventId={props.eventId} />
    </>
  );
};

export default EventDetail;

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { eventId } = context.params as { eventId?: string };

  const tokenSession = getTokenServerSession({
    req: context.req,
    res: context.res,
    query: context.query,
  });

  if (!eventId) {
    return {
      redirect: {
        destination: NAVIGATION_ROUTE.EVENT_LIST.HREF,
        permanent: false,
      },
      props: {},
    };
  }

  const queryClient = new QueryClient();
  await queryClient.prefetchQuery({
    queryKey: eventQueryKeys.getEventDetail({ eventId }),
    queryFn: async () => {
      const { data } = await axios.get<ApiResponseDto<ResponseEventDetailV1>>(
        `${process.env.NEXT_PUBLIC_FIP_API_BASE_URL}/v1/event/${eventId}`,
      );
      return data.data;
    },
    retry: 0,
  });

  const eventDetailData = queryClient.getQueryData<ResponseEventDetailV1>(
    eventQueryKeys.getEventDetail({ eventId }),
  );

  return {
    ...(context.query.access && {
      redirect: {
        destination: tokenSession?.redirectUrl,
      },
    }),
    props: {
      ...(await loadLocaleProps(context)),
      eventId,
      eventDetail: eventDetailData || null,
    },
  };
}
